import classNames from "classnames";
import { capitalize } from "lodash";
import { Alert } from "PFComponents/alert";
import { profileHash } from "PFCore/helpers/profile_hash";
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { humanize } from "underscore.string";

import { EY_CERTIFICATES_FIELD_NAMES, getTranslationKey } from "../../helpers/custom_pptx_template";
import { ProfileExportFormatted } from "../../local_export_build_ey_pptx/helpers";
import PagePreview from "../page_preview";
import { Certificates } from "./components/certificates";
import { CustomField } from "./components/custom_field";
import { Field } from "./components/field";
import { Positions } from "./components/positions";
import { Sectors } from "./components/sectors";
import { Skills } from "./components/skills";
import css from "./ey_pptx_preview.module.scss";

type EYPptxPreviewProps = {
  profile: ProfileExportFormatted;
  config: {
    color?: string;
    hidden: object;
    logo?: string;
    isBlindModeOn?: boolean;
    templateKey: string;
  };
  order: string[];
  avatar: string;
  logo: {
    url: string;
    custom: boolean;
  };
};

export const EYPptxPreview = ({ profile, order, config, avatar, logo }: EYPptxPreviewProps) => {
  const { isBlindModeOn, hidden, templateKey } = config;
  const {
    _originalData,
    contactInformationItems,
    customTypes,
    enabled,
    jobTitle,
    name,
    positions,
    summary,
    id
  } = profile;
  const { t } = useTranslation("profiles", { keyPrefix: "export" });

  const translationKey = getTranslationKey(templateKey);
  const skillsFieldNames = ["skills"];
  const otherFieldNames = order.filter(
    (name) =>
      !["summary", "experience", "sectors", ...EY_CERTIFICATES_FIELD_NAMES, ...skillsFieldNames].includes(
        name
      )
  );

  const skillsType = customTypes.find(({ type }) => type.name === skillsFieldNames[0]);
  const filteredCertificatesTypeNames: string[] = order.filter((itemName) =>
    EY_CERTIFICATES_FIELD_NAMES.includes(itemName)
  );
  const filteredCertificatesTypes = customTypes.filter(({ type }) =>
    filteredCertificatesTypeNames.includes(type.name)
  );
  const certificatesTypesToShow = customTypes.filter(
    ({ type, text }) => filteredCertificatesTypeNames.includes(type.name) && text
  );
  const showCertificates = filteredCertificatesTypes.length && certificatesTypesToShow.length > 0;

  return (
    <>
      {enabled === false && (
        <Alert kind="warning" title="Disabled" style={{ marginBottom: 20, marginTop: 20 }}>
          <Trans i18nKey={`${translationKey}.alert`} t={t} components={[<b key="0" />]} />
        </Alert>
      )}
      <PagePreview
        style={{
          padding: "36px 59px 40px 59px",
          fontFamily: "EYInterstate",
          width: 720,
          height: 540,
          minHeight: 540,
          display: "flex"
        }}
      >
        <div className={css.sidebar}>
          {avatar && !isBlindModeOn && <img className={css.avatar} src={avatar} alt="avatar" />}
          <div className={css.sidebarDetails}>
            <Field
              title={isBlindModeOn ? `Candidate ${profileHash(id)}` : name}
              titleClassName={css.profileName}
            >
              <div className={css.jobTitle}>{jobTitle}</div>
              {!isBlindModeOn &&
                [
                  { text: profile.email },
                  ...contactInformationItems.filter(({ type }) => type.includes("mobile_number"))
                ].map((contact, index) => <div key={`contract_information_${index}`}>{contact.text}</div>)}
            </Field>

            {skillsType && (
              <Skills profile={_originalData} skillsType={skillsType} templateKey={templateKey} />
            )}
            {otherFieldNames.map((name) => (
              <CustomField
                key={name}
                customTypes={customTypes}
                names={[name]}
                title={t(
                  `${translationKey}.${name}` as unknown as TemplateStringsArray,
                  humanize(capitalize(name))
                )}
              />
            ))}
            <Sectors customTypes={customTypes} translationKey={translationKey} profile={profile} />
          </div>
        </div>
        <div className={classNames(css.main, { [css.showCertifacates]: showCertificates })}>
          {order.map((name) => {
            if (hidden[name]) {
              return null;
            } else if (name === "summary") {
              return summary ? (
                <Field
                  className={css.summary}
                  text={summary}
                  key={name}
                  title={t(`${translationKey}.summary`)}
                />
              ) : (
                <div />
              );
            } else if (name === "experience") {
              return (
                <Positions
                  className={css.experience}
                  positions={positions}
                  internalExperiences={_originalData.internal_experiences}
                  key={name}
                  title={t(`${translationKey}.positions`)}
                />
              );
            }
          })}
          {showCertificates && (
            <Certificates
              customTypes={customTypes}
              names={order.filter((name) => EY_CERTIFICATES_FIELD_NAMES.includes(name))}
              templateKey={templateKey}
            />
          )}
          <img className={css.logo} src={logo.url} height={40} alt="" />
        </div>
      </PagePreview>
    </>
  );
};
